import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Navbar from "../navbar/navbar";
import Footer from "../footer/footer";
import Background from "../Background/Background";
import {
  fadeInUp, fadeInLeft, fadeInRight,
  staggerContainer, scaleIn
} from "../../animations/animations";

// Icons
import { FiCode, FiUser, FiMail, FiPhone, FiMapPin,
         FiGlobe, FiCalendar, FiAward, FiBookOpen, FiZap} from "react-icons/fi";
import { FaGithub, FaLinkedin, FaDocker, FaNodeJs,
         FaReact, FaPython, FaBootstrap } from "react-icons/fa";
import {
  SiKubernetes, SiMongodb, SiJavascript, SiTypescript,
  SiRedis, SiApachekafka, SiSocketdotio, SiExpress,
  SiElectron, SiWebrtc, SiHtml5, SiJsonwebtokens
} from "react-icons/si";

// Assets
import profileImage from "../../assets/img/profile.jpg";
import "./profile.css";

function AnimatedCounter({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

// ─── Skill Ring Card (SVG circular progress) ──────────────────
function SkillRing({ name, percent, color = "#6366f1", icon: Icon, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <motion.div
      ref={ref}
      className="skill-ring-card"
      variants={scaleIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
      whileHover={{ y: -6, boxShadow: `0 16px 40px rgba(${color.replace('#','').match(/.{2}/g)?.map(h=>parseInt(h,16)).join(',')}, 0.25)` }}
    >
      {/* SVG Ring */}
      <div className="skill-ring-svg-wrap">
        <svg width="96" height="96" viewBox="0 0 96 96" className="skill-ring-svg">
          {/* Track circle */}
          <circle
            cx="48" cy="48" r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="5"
          />
          {/* Progress circle */}
          <motion.circle
            cx="48" cy="48" r={radius}
            fill="none"
            stroke={color}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={inView ? { strokeDashoffset } : { strokeDashoffset: circumference }}
            transition={{ duration: 1.4, ease: "easeOut", delay: delay + 0.15 }}
            style={{ transform: "rotate(-90deg)", transformOrigin: "48px 48px", filter: `drop-shadow(0 0 5px ${color}88)` }}
          />
        </svg>
        {/* Center icon + percent */}
        <div className="skill-ring-center">
          {Icon && <Icon size={18} style={{ color, marginBottom: 2 }} />}
          <motion.span
            className="skill-ring-percent"
            style={{ color }}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: delay + 0.6 }}
          >
            {percent}%
          </motion.span>
        </div>
      </div>

      <div className="skill-ring-name">{name}</div>

      {/* Glow bg */}
      <div className="skill-ring-glow" style={{ background: `radial-gradient(circle, ${color}18, transparent 70%)` }} />
    </motion.div>
  );
}

// ─── Tech Card ─────────────────────────────────────────────────
function TechCard({ icon: Icon, name, description, link, color, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.a
      ref={ref}
      href={link}
      target="_blank"
      rel="noreferrer"
      className="tech-card"
      variants={scaleIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
      whileHover={{
        y: -6,
        borderColor: `rgba(${color}, 0.5)`,
        boxShadow: `0 16px 40px rgba(${color}, 0.18)`,
      }}
    >
      <div className="tech-card-icon" style={{ color: `rgb(${color})`, background: `rgba(${color},0.1)` }}>
        <Icon size={24} />
      </div>
      <div className="tech-card-info">
        <h4 className="tech-card-name">{name}</h4>
        <p className="tech-card-desc">{description}</p>
      </div>
      <div className="tech-card-border" style={{ background: `rgba(${color},0.6)` }} />
    </motion.a>
  );
}

// ─── PROFILE ───────────────────────────────────────────────────
function Profile({ currentPage, isAIChatOpen, openAIChat, theme, toggleTheme }) {
  const { ref: profileRef, inView: profileInView } = useInView({ triggerOnce: true, threshold: 0.2 });
  const { ref: statsRef, inView: statsInView } = useInView({ triggerOnce: true, threshold: 0.3 });

  const techStack = [
    { icon: FaDocker,      name: "Docker",     description: "Container platform for building & sharing apps.", link: "https://www.docker.com/",     color: "23,152,220" },
    { icon: SiKubernetes,  name: "Kubernetes",  description: "Open-source container orchestration system.",    link: "https://kubernetes.io/",     color: "50,108,229" },
    { icon: SiJavascript,  name: "JavaScript",  description: "Lightweight, interpreted programming language.", link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript", color: "240,219,79" },
    { icon: SiMongodb,     name: "MongoDB",     description: "Open source NoSQL database management.",        link: "https://www.mongodb.com/",    color: "77,179,61" },
    { icon: FaNodeJs,      name: "Node.js",     description: "Cross-platform JavaScript runtime environment.", link: "https://nodejs.org/en",       color: "104,194,85" },
    { icon: FaReact,       name: "React JS",    description: "Front-end library for building UI components.", link: "https://react.dev/",          color: "97,218,251" },
    { icon: SiExpress,     name: "Express JS",  description: "Fast, minimalist web framework for Node.js.",  link: "https://expressjs.com/",      color: "200,200,200" },
    { icon: SiElectron,    name: "Electron",    description: "Build cross-platform desktop apps with web.",   link: "https://www.electronjs.org/", color: "47,213,255" },
    { icon: SiWebrtc,      name: "WebRTC",      description: "Real-time communication in web browsers.",      link: "https://webrtc.org/",         color: "99,236,121" },
    { icon: SiSocketdotio, name: "Socket.IO",   description: "Low-latency, event-based communication.",       link: "https://socket.io/",          color: "200,200,200" },
    { icon: SiTypescript,  name: "TypeScript",  description: "JavaScript with syntax for types.",             link: "https://www.typescriptlang.org/", color: "49,120,198" },
    { icon: FaPython,      name: "Python",      description: "High-level, general-purpose programming lang.", link: "https://www.python.org/",     color: "55,118,171" },
    { icon: SiRedis,       name: "Redis",       description: "In-memory data structure store & cache.",       link: "https://redis.io/",           color: "215,48,48" },
    { icon: SiApachekafka, name: "Kafka",       description: "Build real-time streaming data pipelines.",     link: "https://kafka.apache.org/",   color: "200,200,200" },
    { icon: SiHtml5,       name: "HTML",        description: "Standard markup language for web browsers.",    link: "https://developer.mozilla.org/en-US/docs/Web/HTML", color: "228,77,38" },
    { icon: SiJsonwebtokens, name: "JWT",       description: "Standardized method for securely sharing claims.", link: "https://jwt.io/",           color: "210,50,45" },
    { icon: FaBootstrap,   name: "Bootstrap",   description: "Free CSS framework for responsive development.", link: "https://getbootstrap.com/",  color: "121,82,179" },
  ];

  const skills = [
    { name: "HTML",         percent: 65, color: "#f97316", icon: SiHtml5,      delay: 0    },
    { name: "Node.js",      percent: 70, color: "#22c55e", icon: FaNodeJs,     delay: 0.08 },
    { name: "JavaScript",   percent: 60, color: "#eab308", icon: SiJavascript, delay: 0.16 },
    { name: "React JS",     percent: 70, color: "#06b6d4", icon: FaReact,      delay: 0.04 },
    { name: "Docker/K8s",   percent: 60, color: "#3b82f6", icon: FaDocker,     delay: 0.12 },
    { name: "MongoDB",      percent: 80, color: "#10b981", icon: SiMongodb,    delay: 0.20 },
  ];

  const stats = [
    { value: 3, suffix: "+", label: "Years Exp." },
    { value: 10, suffix: "+", label: "Projects" },
    { value: 17, suffix: "+", label: "Technologies" },
  ];

  return (
    <>
      <Background />
      <Navbar
        currentPage={currentPage}
        isAIChatOpen={isAIChatOpen}
        openAIChat={openAIChat}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <div className="profile-page">

        {/* ═══════════════ ABOUT / HERO ═══════════════ */}
        <section className="profile-hero">
          {/* Left — profile image */}
          <motion.div
            className="profile-image-col"
            ref={profileRef}
            variants={fadeInLeft}
            initial="hidden"
            animate={profileInView ? "visible" : "hidden"}
          >
            <div className="profile-img-wrapper">
              <motion.div
                className="profile-img-ring"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="profile-img-ring ring-2"
                animate={{ rotate: -360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              />
              <motion.img
                src={profileImage}
                alt="Shabin SV"
                className="profile-img"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="profile-img-glow"
                animate={{ opacity: [0.4, 0.8, 0.4], scale: [1, 1.1, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>

            {/* Quick stats below image */}
            <motion.div
              className="profile-quick-stats"
              ref={statsRef}
              variants={staggerContainer}
              initial="hidden"
              animate={statsInView ? "visible" : "hidden"}
            >
              {stats.map((s) => (
                <motion.div key={s.label} className="profile-stat" variants={scaleIn}>
                  <div className="profile-stat-number">
                    <AnimatedCounter target={s.value} suffix={s.suffix} />
                  </div>
                  <div className="profile-stat-label">{s.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right — bio */}
          <motion.div
            className="profile-bio-col"
            variants={fadeInRight}
            initial="hidden"
            animate={profileInView ? "visible" : "hidden"}
            transition={{ delay: 0.1 }}
          >
            <span className="profile-section-badge">
              <FiUser size={12} /> About Me
            </span>

            <h1 className="profile-name">
              <span className="profile-name-gradient">Shabin SV</span>
            </h1>
            <h2 className="profile-role">Full Stack Web Developer</h2>

            <p className="profile-bio">
              Versatile full-stack developer with strong proficiency in frontend and backend
              technologies. Dedicated to delivering efficient, scalable web solutions. Experienced
              in crafting dynamic, user-centric applications with innovation and precision.
            </p>
            <p className="profile-bio">
              A passionate developer with a background in Full Stack Web Development, honing skills
              in JavaScript, Node.js, React.js, Docker, and Kubernetes technologies.
            </p>

            {/* Info grid */}
            <div className="profile-info-grid">
              {[
                { icon: FiCalendar, label: "Birthday",  value: "16 Jul 1996" },
                { icon: FiMapPin,   label: "City",      value: "Trivandrum, Kerala" },
                { icon: FiBookOpen, label: "Degree",    value: "B-Tech" },
                { icon: FiGlobe,    label: "Website",   value: "www.shabinsv.in", href: "https://shabinsv.in" },
                { icon: FiPhone,    label: "Phone",     value: "+91 77736107770", href: "callto:+9177736107770" },
                { icon: FiMail,     label: "Email",     value: "shabinsv7@gmail.com", href: "mailto:shabinsv7@gmail.com" },
              ].map((item) => (
                <div key={item.label} className="profile-info-item">
                  <span className="profile-info-icon">
                    <item.icon size={14} />
                  </span>
                  <div>
                    <div className="profile-info-label">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="profile-info-value profile-info-link" target={item.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
                        {item.value}
                      </a>
                    ) : (
                      <div className="profile-info-value">{item.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social */}
            <div className="profile-socials">
              <motion.a
                href="https://in.linkedin.com/in/shabin-sv-831872228"
                target="_blank"
                rel="noreferrer"
                className="profile-social-btn linkedin"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.96 }}
              >
                <FaLinkedin /> LinkedIn
              </motion.a>
              <motion.a
                href="https://github.com/shabinsv"
                target="_blank"
                rel="noreferrer"
                className="profile-social-btn github"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.96 }}
              >
                <FaGithub /> GitHub
              </motion.a>
              <motion.a
                href="mailto:shabinsv7@gmail.com"
                className="profile-social-btn email"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.96 }}
              >
                <FiMail /> Email Me
              </motion.a>
            </div>
          </motion.div>
        </section>

        {/* ═══════════════ SKILLS ═══════════════ */}
        <section className="profile-section-wrap">
          <motion.div
            className="profile-section-header"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="profile-section-badge"><FiCode size={12} /> Skills</span>
            <h2 className="profile-section-title">Technical <span className="gradient-text">Proficiency</span></h2>
          </motion.div>

          <div className="skills-rings-grid">
            {skills.map((skill) => (
              <SkillRing
                key={skill.name}
                name={skill.name}
                percent={skill.percent}
                color={skill.color}
                icon={skill.icon}
                delay={skill.delay}
              />
            ))}
          </div>
        </section>

        {/* ═══════════════ AI WORKFLOW ═══════════════ */}
        <section className="profile-section-wrap">
          <motion.div
            className="profile-section-header"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="profile-section-badge"><FiZap size={12} /> AI-Powered Development</span>
            <h2 className="profile-section-title">Integrating <span className="gradient-text">AI</span> into My Workflow</h2>
            <p className="profile-section-sub">Leveraging artificial intelligence to enhance productivity, code quality, and development speed</p>
          </motion.div>

          <div className="ai-workflow-content">
            <motion.div
              className="ai-workflow-text"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="ai-workflow-bullets">
                <motion.div
                  className="ai-bullet"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <span className="ai-bullet-icon">⚡</span>
                  <div>
                    <strong>Code Generation & Optimization</strong>
                    <p>Utilizing AI to generate boilerplate code, optimize algorithms, and implement best practices automatically.</p>
                  </div>
                </motion.div>
                
                <motion.div
                  className="ai-bullet"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <span className="ai-bullet-icon">🐛</span>
                  <div>
                    <strong>Intelligent Debugging</strong>
                    <p>AI-powered error analysis and debugging assistance that identifies root causes and suggests optimal solutions.</p>
                  </div>
                </motion.div>
                
                <motion.div
                  className="ai-bullet"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <span className="ai-bullet-icon">🔍</span>
                  <div>
                    <strong>Research & Learning</strong>
                    <p>Accelerating technology research, staying current with best practices, and rapidly learning new frameworks.</p>
                  </div>
                </motion.div>
              </div>
              
            </motion.div>
            
            <motion.div
              className="ai-workflow-visual"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="ai-visual-card">
                <div className="ai-visual-header">
                  <span className="ai-visual-dot green" />
                  <span className="ai-visual-dot yellow" />
                  <span className="ai-visual-dot red" />
                  <span className="ai-visual-title">AI Integration</span>
                </div>
                <div className="ai-visual-content">
                  <div className="ai-metric">
                    <span className="ai-metric-label">Development Speed</span>
                    <span className="ai-metric-value">+40%</span>
                  </div>
                  <div className="ai-metric">
                    <span className="ai-metric-label">Code Quality</span>
                    <span className="ai-metric-value">+35%</span>
                  </div>
                  <div className="ai-metric">
                    <span className="ai-metric-label">Problem Solving</span>
                    <span className="ai-metric-value">+50%</span>
                  </div>
                </div>
                <div className="ai-visual-glow" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════ TECH STACK ═══════════════ */}
        <section className="profile-section-wrap">
          <motion.div
            className="profile-section-header"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="profile-section-badge"><FiAward size={12} /> Experience In</span>
            <h2 className="profile-section-title">Technology <span className="gradient-text">Stack</span></h2>
            <p className="profile-section-sub">Technologies I've worked with and continue to explore</p>
          </motion.div>

          <div className="tech-cards-grid">
            {techStack.map((tech, i) => (
              <TechCard
                key={tech.name}
                {...tech}
                delay={i * 0.04}
              />
            ))}
          </div>
        </section>

      </div>

      <Footer />
    </>
  );
}

export default Profile;